import { useTranslations } from "use-intl";
import { useEffect, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { beep, vibrate } from "@/lib/feedback";
import { cn } from "@/lib/utils";

export type RestState = {
  endsAt: number;
  total: number;
  nextLabel: string;
  /** Set id to ask "reps in reserve" for, if any. */
  askRirFor: string | null;
};

const RIR_OPTIONS = [0, 1, 2, 3, 4];

export function RestOverlay({
  state,
  sound,
  vibration,
  beginner,
  currentRir,
  onRir,
  onChange,
  onClose,
}: {
  state: RestState;
  sound: boolean;
  vibration: boolean;
  beginner: boolean;
  currentRir: number | null;
  onRir: (rir: number) => void;
  onChange: (s: RestState) => void;
  onClose: () => void;
}) {
  const t = useTranslations("train");
  const [now, setNow] = useState(() => Date.now());
  const fired = useRef(false);
  const remaining = Math.max(0, Math.ceil((state.endsAt - now) / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (remaining > 0 || fired.current) return;
    fired.current = true;
    if (sound) beep();
    if (vibration) vibrate();
    const id = setTimeout(onClose, 1200);
    return () => clearTimeout(id);
  }, [remaining, sound, vibration, onClose]);

  const pct = Math.min(1, remaining / Math.max(1, state.total));
  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, "0");
  const adjust = (sec: number) => {
    fired.current = false;
    onChange({ ...state, endsAt: Math.max(Date.now(), state.endsAt + sec * 1000), total: Math.max(1, state.total + sec) });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-xl animate-in fade-in">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-6 pt-safe">
        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">{t("restTitle")}</p>
        <div className="relative mt-6 flex size-64 items-center justify-center">
          <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
            <circle cx="50" cy="50" r="45" className="fill-none stroke-muted" strokeWidth="5" />
            <circle
              cx="50"
              cy="50"
              r="45"
              className={cn("fill-none transition-[stroke-dashoffset] duration-200", remaining === 0 ? "stroke-success" : "stroke-primary")}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={282.7}
              strokeDashoffset={282.7 * (1 - pct)}
            />
          </svg>
          <span className="text-7xl font-black tabular-nums">
            {mm}:{ss}
          </span>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Button variant="secondary" size="lg" className="h-12 rounded-2xl" onClick={() => adjust(-15)}>
            <Minus /> 15 s
          </Button>
          <Button variant="secondary" size="lg" className="h-12 rounded-2xl" onClick={() => adjust(15)}>
            <Plus /> 15 s
          </Button>
        </div>
        <p className="mt-8 text-sm text-muted-foreground">{t("upNext")}</p>
        <p className="text-center text-lg font-semibold">{state.nextLabel}</p>

        {state.askRirFor && (
          <div className="mt-8 w-full rounded-2xl border bg-card p-4">
            <p className="text-center text-sm font-semibold">{t("rirQuestion")}</p>
            {beginner && <p className="mt-0.5 text-center text-xs text-muted-foreground">{t("rirHelp")}</p>}
            <div className="mt-3 grid grid-cols-5 gap-2">
              {RIR_OPTIONS.map((r) => (
                <Button
                  key={r}
                  variant={currentRir === r ? "default" : "outline"}
                  className="h-11 rounded-xl text-base"
                  onClick={() => onRir(r)}
                >
                  {r === 4 ? "4+" : r}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="mx-auto w-full max-w-md px-6 pb-safe">
        <Button size="lg" className="mb-2 h-14 w-full rounded-2xl text-base" onClick={onClose}>
          {t("restSkip")}
        </Button>
      </div>
    </div>
  );
}
